import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Box, IconButton } from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import placeHolderImage from '../../assets/Placeholder.svg';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import { observer } from "mobx-react";

const useStyles = makeStyles(() => ({

  navButtonsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    width: "calc(100% - 108px)"
  },
  photoControls: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    gap: "8px",
    bottom: 10,
    left: "50%",
    transform: "translateX(-50%)"
  },
  zoomControl: {
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.8)",
    },
    width: "30px",
    height: "30px"
  },
  caption: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    gap: "8px",
    bottom: '50px',
    left: "50%",
    transform: "translateX(-50%)",
    background: 'rgba(0, 0, 0, 0.5)',
    padding: '16px 32px',
    borderRadius: '8px',
    color: 'white'
  },
  resetButton: {
    "& .MuiIconButton-label": {
      width: "30px",
      height: "30px"
    }
  },
  currentIndex: {
    backgroundColor: "#464a51",
    color: "white",
    borderRadius: "10px",
    padding: "6px 20px",
    fontSize: "16px",
    fontWeight: 500,
  },
  transformContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    "& > div": {
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    }
  },
  mainImgContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f5f5f5",
    borderRadius: "8px",
  },
  mainImg: {
    maxWidth: "100%",
    height: "100%",
    objectFit: "contain",
  },
  thumbnailImage: {
    width: "100px",
    height: "100px",
    objectFit: "cover",
    cursor: "pointer",
    borderRadius: "6px",
    backgroundColor: "#f0f0f0"
  },
  swiperContainer: {
    display: "flex",
    flexDirection: "row",
    gridGap: "8px",
    width: "100%",
    height: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    "& *, & *::before, & *::after": {
      boxSizing: "inherit",
    },
    "& .swiper": {
      margin: 0,
      width: "100%",
    },
  }
}));

export interface PhotoGalleryImage {
  key: string;
  url: string;
  preferredRotation?: number;
  caption?: string;
}

interface PhotoGalleryProps {
  selectedPhotoKey?: string;
  photos: PhotoGalleryImage[];
  enableZoom?: boolean;
  minZoomScale?: number;
  maxZoomScale?: number;
  enableRotation?: boolean;
  onPhotoRotation?: (photoKey: string, rotationDegrees: number) => void;
  onPhotoChange?: (photoKey: string) => void;
}

export const PhotoGallery = ({
  selectedPhotoKey,
  photos,
  enableZoom = true,
  minZoomScale = 0.5,
  maxZoomScale = 4,
  enableRotation = true,
  onPhotoRotation,
  onPhotoChange,
}: PhotoGalleryProps) => {
  console.log(photos?.[0]?.url);
  const swiperRef = useRef(null);
  const thumbsSwiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const classes = useStyles();

  const minZoomScaleValue = enableZoom ? minZoomScale : 1;
  const maxZoomScaleValue = enableZoom ? maxZoomScale : 1;

  const onError = (event) => {
    event.target.src = placeHolderImage;
  };

  useEffect(() => {
    if (selectedPhotoKey) {
      const selectedImageIndex = photos.findIndex(photo => photo.key === selectedPhotoKey);
      if (swiperRef?.current && thumbsSwiperRef?.current && selectedImageIndex > 0) {
        thumbsSwiperRef.current?.slideTo(selectedImageIndex, 0, false);
        swiperRef.current?.slideTo(selectedImageIndex, 0, false);
        setActiveIndex(selectedImageIndex);
      }
    }
  }, [selectedPhotoKey]);

  const changeSlideHandler = (index) => {
    if (index < 0 || index >= photos.length) {
      return;
    }
    if (!onPhotoRotation || !enableRotation) {
      setRotationDegrees(0);
    }
    setActiveIndex(index);
    thumbsSwiperRef.current?.slideTo(index);
    swiperRef.current?.slideTo(index);
  }

  const rotateLeft = () => {
    if (onPhotoRotation && enableRotation) {
      const prev = photos[activeIndex].preferredRotation || 0;
      const newRotation = (prev + 270) % 360;
      onPhotoRotation(photos[activeIndex].key, newRotation);
    } else {
      setRotationDegrees(prevValue => {
        return (prevValue + 270) % 360;
      });
    }
  };

  const rotateRight = () => {
    if (onPhotoRotation && enableRotation) {
      const prev = photos[activeIndex].preferredRotation || 0;
      const newRotation = (prev + 90) % 360;
      onPhotoRotation(photos[activeIndex].key, newRotation);
    } else {
      setRotationDegrees(prevValue => {
        return (prevValue + 90) % 360;
      });
    }
  };

  const onSlideChange = (swiper) => {
    changeSlideHandler(swiper.activeIndex);
    if(onPhotoChange){
      onPhotoChange(photos[swiper.activeIndex].key);
    }
  }

  const displayPhotoControls = enableZoom || enableRotation;
  const activeImageRotationDegrees = onPhotoRotation && enableRotation ? photos[activeIndex].preferredRotation || 0 : rotationDegrees;

  return (
    <Box display="flex" flexDirection="column" gridGap="8px" height="100%">
      <Box style={{ height: "calc(100% - 56px)" }}>
        <Box className={classes.swiperContainer}>
          {/** Main Swiper */}
          <Box flexGrow="1" style={{ minWidth: 0, height: "100%", position: "relative" }}>
            <Swiper
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              onSlideChange={onSlideChange}
              allowTouchMove={false}
              slidesPerView={1}
              spaceBetween={0}
              style={{ width: "100%", height: "100%" }}
            >
              {photos.map((image, i) => (
                <SwiperSlide key={i} style={{ height: "100%" }}>
                  <TransformWrapper
                    initialScale={1}
                    minScale={minZoomScaleValue}
                    maxScale={maxZoomScaleValue}
                    wheel={{ step: 0.1 }}
                  >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                      <>
                        <Box className={classes.mainImgContainer}>
                          <TransformComponent wrapperClass={classes.transformContainer}>
                            <img
                              src={image.url}
                              alt={`Zoomable ${i + 1}`}
                              className={classes.mainImg}
                              onError={onError}
                              style={{
                                transform: `rotate(${activeImageRotationDegrees}deg)`
                              }}
                            />
                          </TransformComponent>
                        </Box>
                        {Boolean(image.caption) && (
                          <Box className={classes.caption}>
                            {image.caption}
                          </Box>
                        )}
                        {displayPhotoControls && (<Box className={classes.photoControls}>
                          {enableZoom && (
                            <>
                              <IconButton title="Zoom in" className={classes.zoomControl} onClick={() => zoomIn()}> <ZoomInIcon /></IconButton>
                              <IconButton title="Zoom out" className={classes.zoomControl} onClick={() => zoomOut()}> <ZoomOutIcon /></IconButton>
                              <IconButton title="Reset zoom" className={classes.zoomControl} onClick={() => resetTransform()}> <SettingsBackupRestoreIcon /></IconButton>
                            </>)}
                          {enableRotation && (
                            <>
                              <IconButton title="Rotate left" className={classes.zoomControl} onClick={() => rotateLeft()}> <RotateLeftIcon /></IconButton>
                              <IconButton title="Rotate right" className={classes.zoomControl} onClick={() => rotateRight()}> <RotateRightIcon /></IconButton>
                            </>
                          )}
                        </Box>)}
                      </>
                    )}
                  </TransformWrapper>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>

          {/* Thumbnails */}
          <Box>
            <Swiper
              onSwiper={(swiper) => (thumbsSwiperRef.current = swiper)}
              direction={"vertical"}
              pagination={{
                clickable: true,
              }}
              slidesPerView="auto"
              style={{ height: "100%" }}
              spaceBetween={8}
            >
              {photos.map((image, index) => (
                <SwiperSlide key={index} style={{ height: "100px" }}>
                  <img
                    src={image.url}
                    alt={`thumb-${index}`}
                    onClick={() => changeSlideHandler(index)}
                    className={classes.thumbnailImage}
                    onError={onError}
                    style={{
                      opacity: "1",
                      transform: `rotate(${image.preferredRotation}deg)`,
                      border:  index === activeIndex ? "4px solid #6971d6" : "none"
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Box>
      </Box>
      <Box>
        {/** Nav Buttons */}
        <Box className={classes.navButtonsContainer}>
          <IconButton onClick={() => changeSlideHandler(activeIndex - 1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box className={classes.currentIndex}>{activeIndex + 1} of {photos.length}</Box>
          <IconButton onClick={() => changeSlideHandler(activeIndex + 1)}>
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};